// src/utils/country.js
// Country helpers for Nations standings + flags. Country comes from the device
// TIME ZONE (a good proxy, needs no location permission and no server), with
// the browser locale region as a fallback.
import TZ_COUNTRY from './tzCountry.json';

export function getDeviceCountry() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz && TZ_COUNTRY[tz]) return TZ_COUNTRY[tz];
  } catch { /* ignore */ }
  try {
    const loc = navigator.language || '';
    const m = loc.match(/[-_]([A-Za-z]{2})$/);
    if (m) return m[1].toUpperCase();
  } catch { /* ignore */ }
  return null;
}

// --- Team override (Nations battle) -----------------------------------------
// "Where a night HAPPENS" (world map / sessions) stays device-derived, but
// "who you FIGHT FOR" (Nations standings, your leaderboard flag) is a choice:
// a Canadian on a Copacabana bender still banks the night for Canada. Defaults
// to the device country until the user picks a flag on the Nations tab.
const TEAM_COUNTRY_KEY = 'bender_team_country';

export function getTeamCountry() {
  try {
    const cc = localStorage.getItem(TEAM_COUNTRY_KEY);
    if (cc && /^[A-Z]{2}$/.test(cc)) return cc;
  } catch { /* ignore */ }
  return getDeviceCountry();
}

export function setTeamCountry(cc) {
  try {
    if (cc && /^[A-Z]{2}$/.test(cc)) localStorage.setItem(TEAM_COUNTRY_KEY, cc);
    else localStorage.removeItem(TEAM_COUNTRY_KEY); // back to auto (device)
  } catch { /* ignore */ }
}

export function isTeamOverridden() {
  try { return /^[A-Z]{2}$/.test(localStorage.getItem(TEAM_COUNTRY_KEY) || ''); } catch { return false; }
}

// Every country that appears in the tz map — the pickable team list.
let _allCodes = null;
export function allCountryCodes() {
  if (!_allCodes) _allCodes = [...new Set(Object.values(TZ_COUNTRY))].sort();
  return _allCodes;
}

export function flagEmoji(cc) {
  if (!cc || cc.length !== 2) return '🏳️';
  const A = 0x1F1E6;
  return String.fromCodePoint(A + cc.charCodeAt(0) - 65, A + cc.charCodeAt(1) - 65);
}

let _names = null;
export function countryName(cc) {
  if (!cc) return 'Unknown';
  try {
    if (!_names) _names = new Intl.DisplayNames([navigator.language || 'en'], { type: 'region' });
    return _names.of(cc) || cc;
  } catch { return cc; }
}

// Approximate 2024 populations in millions — for per-capita standings only.
export const POPULATION_M = {
  US: 335, CA: 40, GB: 68, IE: 5.3, AU: 26.6, NZ: 5.2, DE: 84, FR: 68, ES: 48, IT: 59,
  PT: 10.5, NL: 18, BE: 11.7, MX: 129, BR: 216, AR: 46, CL: 19.6, CO: 52, JP: 124, KR: 52,
  CN: 1410, IN: 1430, SE: 10.5, NO: 5.5, DK: 5.9, FI: 5.6, PL: 38, CZ: 10.8, AT: 9.1, CH: 8.8,
  ZA: 60, NG: 224, EG: 105, TR: 85, IL: 9.8, AE: 9.5, SA: 36, TH: 72, VN: 99, PH: 117,
  ID: 278, MY: 34, SG: 5.9, RU: 144, UA: 37, GR: 10.4, HU: 9.6, RO: 19, HR: 3.9, RS: 6.7,
  PE: 34, CR: 5.2, PA: 4.5, DO: 11.3, PR: 3.2, JM: 2.8, IS: 0.39, LU: 0.66, MT: 0.54, EE: 1.4,
  LV: 1.9, LT: 2.8, SK: 5.4, SI: 2.1, BG: 6.7, HK: 7.5, TW: 23.4, KE: 55, MA: 37, QA: 2.7,
};

export function perCapita(value, cc) {
  const pop = POPULATION_M[cc];
  return pop ? value / pop : null; // per million people
}
